// Pure day <-> pixel mapping. This is the single source of truth for
// horizontal placement — every glyph, connector, gridline and ruler must
// derive its x position from here, never recompute it independently, or
// connectors will visibly detach from the glyphs they point at.
export type DayScale = {
  dayMin: number;
  dayMax: number;
  colWidth: number;
  labelWidth: number;
  numDays: number;
  totalWidth: number;
  xOfDayEdge: (day: number) => number;
  xOfDayCenter: (day: number) => number;
  dayAtX: (x: number) => number;
};

export const createDayScale = (opts: {
  dayMin: number;
  dayMax: number;
  colWidth: number;
  labelWidth: number;
}): DayScale => {
  const { colWidth, labelWidth } = opts;
  const dayMin = Math.min(opts.dayMin, opts.dayMax);
  const dayMax = Math.max(opts.dayMin, opts.dayMax);
  const numDays = dayMax - dayMin + 1;
  const totalWidth = labelWidth + numDays * colWidth;

  const xOfDayEdge = (day: number) => labelWidth + (day - dayMin) * colWidth;
  const xOfDayCenter = (day: number) => xOfDayEdge(day) + colWidth / 2;
  const dayAtX = (x: number) =>
    dayMin + Math.floor((x - labelWidth) / colWidth);

  return {
    dayMin,
    dayMax,
    colWidth,
    labelWidth,
    numDays,
    totalWidth,
    xOfDayEdge,
    xOfDayCenter,
    dayAtX
  };
};

// Column width responsive to available width, clamped to the zoom level's
// minimum — mirrors the design spec's `max(minColWidth, availableWidth / numDays)`.
export const computeColWidth = (opts: {
  containerWidth: number;
  labelWidth: number;
  numDays: number;
  minColWidth: number;
}) => {
  const { containerWidth, labelWidth, numDays, minColWidth } = opts;
  if (numDays <= 0) {
    return minColWidth;
  }
  return Math.max(minColWidth, (containerWidth - labelWidth) / numDays);
};
